<template>
  <CustomDrawer
    v-model:modelValue="internalVisible"
    direction="right"
    width="600px"
    contentCSS="custom-content-css"
    :title="mode === 'create' ? 'Create Email Template' : 'Edit Email Template'"
  >
    <div class="flex flex-col h-full">
      <div
        class="flex flex-col gap-6 text-[13px] secondary_color proxima_nova font-normal"
      >
        <div id="template_name" class="form_col">
          <span>Template Name</span>
          <a-input
            ref="templateNameInputRef"
            class="input_medium"
            v-model:value="internalForm.name"
            placeholder="Enter Template Name"
            allow-clear
          />
        </div>
        <div id="template_subject" class="form_col">
          <span>Subject</span>
          <div class="relative w-full">
            <a-input
              class="input_medium"
              v-model:value="internalForm.subject"
              placeholder="Enter Subject"
              @focus="inputInFocus = 'subject'"
              ref="subjectInputRef"
              allow-clear
          />
            <div class="absolute top-[7px] right-[10px] flex items-center">
              <PersonalizationDropdown
                @apply-personalization="applyPersonalization"
                position="start"
                :isSubjectField="true"
                :onHover="true"
              />
            </div>
          </div>
        </div>

        <div id="template_body" class="form_col flex-grow flex flex-col">
          <span>Body</span>
          <div class="quill_editor_container">
            <QuillEditor
              v-model:modelValue="internalForm.body"
              class="w-full h-full"
              ref="quillEditorRef"
              @editor-click="inputInFocus = 'quill'"
            />

            <div class="flex flex-row justify-end mb-1 mr-2">
              <PersonalizationDropdown
                @apply-personalization="applyPersonalization"
                position="topend"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="mt-2 flex flex-row gap-2 w-full">
        <a-input
          v-model:value="testEmail"
          placeholder="Enter email for test"
          class="input_medium flex-grow"
          allow-clear
        />
        <a-button
          class="secondary_medium"
          @click="sendTest"
          :loading="isSendingTest"
        >
          Send Test
        </a-button>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end">
        <a-button class="primary_medium" @click="handleSave">
          {{ mode === "create" ? "Create Template" : "Update Template" }}
        </a-button>
      </div>
    </template>
  </CustomDrawer>
</template>

<script setup>
import { ref, watch, computed, nextTick } from "vue";
import { message } from "ant-design-vue";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import QuillEditor from "@/src/components/quill_editor/QuillEditor.vue";
import PersonalizationDropdown from "@/src/components/PersonalizationDropdown.vue";
import DashboardService from "@/src/api/DashboardService.js";

const props = defineProps({
  visible: { type: Boolean, required: true },
  mode: { type: String, required: true },
  templateForm: { type: Object, required: true },
});

const emit = defineEmits(["update:visible", "save"]);

const internalVisible = computed({
  get: () => props.visible,
  set: (value) => emit("update:visible", value),
});

const internalForm = ref({ ...props.templateForm });
const testEmail = ref("");
const isSendingTest = ref(false);
const inputInFocus = ref("quill");
const subjectInputRef = ref(null);
const quillEditorRef = ref(null);
const templateNameInputRef = ref(null);

const handleSave = () => {
  emit("save", internalForm.value);
};

const sendTest = async () => {
  if (!testEmail.value) {
    message.error("Please enter an email address for the test");
    return;
  }
  isSendingTest.value = true;
  try {
    const formatEmails = (emails) => emails.map((email) => ({ email }));

    const payload = {
      from: null,
      to: formatEmails([testEmail.value]),
      subject: internalForm.value.subject,
      message: internalForm.value.body,
      cc: formatEmails([]),
      bcc: formatEmails([]),
      attachment_file_ids: [],
    };
    console.log(payload);
    const response = await DashboardService.sendEmail(payload);
    if (response.success) {
      message.success("Test email sent successfully");
    } else {
      message.error("Failed to send test email");
    }
  } catch (error) {
    console.error("Error sending test email:", error);
    message.error("An error occurred while sending the test email");
  } finally {
    isSendingTest.value = false;
  }
};

const applyPersonalization = (tag) => {
  if (inputInFocus.value === "subject") {
    const input = subjectInputRef.value.input;
    const cursorPosition = input.$el.selectionStart;
    const currentValue = internalForm.value.subject || "";
    internalForm.value.subject =
      currentValue.slice(0, cursorPosition) +
      tag +
      currentValue.slice(cursorPosition);
    nextTick(() => {
      input.setSelectionRange(
        cursorPosition + tag.length,
        cursorPosition + tag.length,
      );
      input.focus();
    });
  } else if (inputInFocus.value === "quill") {
    const quill = quillEditorRef.value.getQuill();
    if (!quill) return;
    quill.focus();
    const range = quill.getSelection();
    const insertPosition = range ? range.index : quill.getLength() - 1;

    quill.insertText(insertPosition, tag);
    quill.setSelection(insertPosition + tag.length, 0);

    internalForm.value.body = quill.root.innerHTML;
  }
};

watch(
  () => props.templateForm,
  (newValue) => {
    internalForm.value = { ...newValue };
  },
  { deep: true },
);

watch(
  () => props.visible,
  (newVal) => {
    nextTick(() => {
      if (newVal && templateNameInputRef.value) {
        templateNameInputRef.value?.focus();
      }
    });
  },
);
</script>
